// src/render/audio/codec.ts — Opus + AAC feature-detect (CONTEXT D-19).
//
// 06-RESEARCH §iOS Safari Specifics: ship .opus as Ogg-Opus (ffmpeg default).
// On iOS <17 (and certain in-app WebViews) Howler.codecs('opus') returns
// false, so AAC is the fallback codec. selectCodec() runs at audio-manager
// init time and stays cached for the session — codec support is a stable
// browser capability, not a per-call probe.
//
// Both URLs (opus + aac) are passed to every Howl as `src: [...]`; Howler
// itself picks the first compatible source. selectCodec() exists for cases
// where we want to know the picked codec for telemetry / DEV overlay /
// future preload-only-one-bundle tuning.

import { Howler } from 'howler';

export type AudioCodec = 'opus' | 'aac';

/**
 * Pick the preferred audio codec for the current browser. Runs a runtime
 * `Howler.codecs('opus')` check — survives iOS Safari updates without
 * requiring a UA-string sniff.
 */
export function selectCodec(): AudioCodec {
  if (Howler.codecs('opus')) return 'opus';
  return 'aac';
}

export interface AudioManifest {
  [assetKey: string]: { opus: string; aac: string };
}

/**
 * Both URLs are returned in [opus, aac] order. Howler iterates `src` and
 * loads the first compatible URL — passing both lets a single bundle
 * survive every browser without any conditional branching at the call site.
 *
 * Throws on unknown manifest key so authoring drift is caught at the
 * caller (audio manager init / stinger preload) rather than failing
 * silently at Howler load time with a generic 404.
 */
export function srcListFor(assetKey: string, manifest: AudioManifest): string[] {
  const entry = manifest[assetKey];
  if (!entry) throw new Error(`audio-manifest missing key: ${assetKey}`);
  return [entry.opus, entry.aac];
}
